import React, { useState, useEffect } from 'react';
import { HiOutlineCurrencyDollar, HiOutlineCalculator } from 'react-icons/hi2';

const Step7_CostAnalysis = ({ incidentData, readOnly = false }) => {
  const [costs, setCosts] = useState(incidentData.costAnalysis || {
    medicalCost: 0,
    equipmentCost: 0,
    productionLossCost: 0,
    environmentalCleanupCost: 0,
    legalFinesCost: 0,
    otherCosts: 0,
    notes: ''
  });

  const [totalCost, setTotalCost] = useState(0);

  useEffect(() => {
    const total =
      (Number(costs.medicalCost) || 0) +
      (Number(costs.equipmentCost) || 0) +
      (Number(costs.productionLossCost) || 0) +
      (Number(costs.environmentalCleanupCost) || 0) +
      (Number(costs.legalFinesCost) || 0) +
      (Number(costs.otherCosts) || 0);
    setTotalCost(total);
  }, [costs]);

  if (!incidentData) return null;

  const handleChange = (field, value) => {
    if (readOnly) return;
    setCosts(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  return (
    <div className="bg-card p-6 rounded-xl border border-gray-200 shadow-sm max-w-4xl mx-auto my-0">
      <div className="border-b border-gray-200 pb-4 mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-md font-bold text-primary flex items-center gap-2">
            <HiOutlineCurrencyDollar className="w-6 h-6 text-green-700" />
            Cost Analysis (Financial Impact)
          </h2>
          <p className="text-tertiary text-sm mt-1">Record the estimated direct and indirect costs associated with this incident.</p>
        </div>
      </div>

      <div className="space-y-8">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

          {/* Direct Costs */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold text-secondary uppercase tracking-wider border-b border-gray-100 pb-2">Direct Costs</h3>

            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Medical & Treatment</label>
              <div className="relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-tertiary sm:text-base">$</span>
                </div>
                <input
                  type="number"
                  className="block w-full border p-2 rounded-md border-gray-300 pl-7 focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
                  value={costs.medicalCost}
                  onChange={(e) => handleChange('medicalCost', e.target.value)}
                  readOnly={readOnly}
                  min="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Equipment Damage / Repair</label>
              <div className="relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-tertiary sm:text-base">$</span>
                </div>
                <input
                  type="number"
                  className="block w-full border p-2 rounded-md border-gray-300 pl-7 focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
                  value={costs.equipmentCost}
                  onChange={(e) => handleChange('equipmentCost', e.target.value)}
                  readOnly={readOnly}
                  min="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Environmental Cleanup</label>
              <div className="relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-tertiary sm:text-base">$</span>
                </div>
                <input
                  type="number"
                  className="block w-full border p-2 rounded-md border-gray-300 pl-7 focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
                  value={costs.environmentalCleanupCost}
                  onChange={(e) => handleChange('environmentalCleanupCost', e.target.value)}
                  readOnly={readOnly}
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Indirect Costs */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold text-secondary uppercase tracking-wider border-b border-gray-100 pb-2">Indirect Costs</h3>

            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Production Loss / Downtime</label>
              <div className="relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-tertiary sm:text-base">$</span>
                </div>
                <input
                  type="number"
                  className="block w-full border p-2 rounded-md border-gray-300 pl-7 focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
                  value={costs.productionLossCost}
                  onChange={(e) => handleChange('productionLossCost', e.target.value)}
                  readOnly={readOnly}
                  min="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Legal, Fines, or Compensation</label>
              <div className="relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-tertiary sm:text-base">$</span>
                </div>
                <input
                  type="number"
                  className="block w-full border p-2 rounded-md border-gray-300 pl-7 focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
                  value={costs.legalFinesCost}
                  onChange={(e) => handleChange('legalFinesCost', e.target.value)}
                  readOnly={readOnly}
                  min="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Other Miscellaneous Costs</label>
              <div className="relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-tertiary sm:text-base">$</span>
                </div>
                <input
                  type="number"
                  className="block w-full border p-2 rounded-md border-gray-300 pl-7 focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
                  value={costs.otherCosts}
                  onChange={(e) => handleChange('otherCosts', e.target.value)}
                  readOnly={readOnly}
                  min="0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Total Cost Presentation */}
        <div className="bg-green-50 border border-green-200 p-3 mb-0 rounded-xl flex items-center justify-between">
          <div>
            <h3 className="text-md mb-0 font-semibold text-green-800 uppercase tracking-wider flex items-center gap-2">
              <HiOutlineCalculator className="w-5 h-5" />
              Total Estimated Financial Impact
            </h3>
          </div>
          <div className="text-3xl font-bold text-green-900">
            {formatCurrency(totalCost)}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-base font-medium text-secondary mb-2">Additional Financial Notes</label>
          <textarea
            rows={3}
            className="w-full border p-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
            placeholder="List any assumptions, pending insurance claims, or qualitative impact..."
            value={costs.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
            readOnly={readOnly}
          />
        </div>

      </div>
    </div>
  );
};

export default Step7_CostAnalysis;
